// How a level name is drawn so that it neither runs into the next name on its
// side of the path nor gets cut off by the top or bottom edge of the path area
// (the path scroller clips anything outside it).
//
// The name sits on the side of its node away from the circle. Its edge next to
// the circle stays put; extra lines grow away from the circle, towards the path
// area's edge. "room" is the space from that inner edge to the path area's edge.

export interface LevelNameFit {
  fontSize: number;
  wraps: boolean;
  // How far the name moves towards its circle, into the label's 10px padding,
  // to stay inside the path area.
  pullTowardNode: number;
}

// The name drawn on one line at one font size. Each size is measured on its
// own: the system font is not drawn in proportion (smaller sizes come out a
// little wider, and line heights are rounded).
export interface NameAtSize {
  fontSize: number;
  oneLineWidth: number;
  // Each word of the name, in order. A wrapped name breaks only between words.
  wordWidths: number[];
  lineHeight: number;
}

export interface LevelNameMeasure {
  // The base font size first, then each size below it.
  sizes: [NameAtSize, ...NameAtSize[]];
  // Widest the name may be before it wraps or shrinks; null for no limit.
  maxWidth: number | null;
  room: number;
}

// No name is drawn smaller than this, nor more than this many pixels below its own size.
const minFontSize = 12;
const maxSizeStep = 4;

// The steps below its own size (0 being the size itself) a name can be drawn at, so only
// these are measured: from 16px, 0 to 4; from 14px, 0 to 2.
export const usableNameSizeSteps = (baseFontSize: number): number[] =>
  Array.from({ length: maxSizeStep + 1 }, (_, step) => step).filter(
    (step) => step === 0 || baseFontSize - step >= minFontSize,
  );
// The least space kept between a name and the path area's edge. Line heights
// are measured at every size, so a name may run right up to the edge.
const pathAreaEdgeGap = 0;
// Of the label's 10px padding between a name and its circle, the most a name may
// give up. The selected node's halo reaches 7px beyond the circle (62px across
// plus its 1px border), so a name pulled this far still clears it.
const maxPullTowardNode = 3;
// Kept spare on each line of a wrapped name, so that rounding never lets the
// browser break a line where this count does not.
const lineWidthSlack = 0.5;

interface Candidate {
  size: NameAtSize;
  wraps: boolean;
}

const spaceWidthOf = ({ oneLineWidth, wordWidths }: NameAtSize): number =>
  wordWidths.length > 1
    ? Math.max(
        0,
        (oneLineWidth - wordWidths.reduce((sum, width) => sum + width, 0)) /
          (wordWidths.length - 1),
      )
    : 0;

// How many lines the name takes at this size when it wraps at maxWidth;
// Infinity when a word is wider than maxWidth on its own.
const wrappedLineCount = (size: NameAtSize, maxWidth: number): number => {
  const lineLimit = maxWidth - lineWidthSlack;
  const spaceWidth = spaceWidthOf(size);
  const lineWidths = size.wordWidths.reduce<number[]>((lines, word) => {
    const current = lines[lines.length - 1];
    if (current === undefined) return [word];
    const joined = current + spaceWidth + word;
    return joined > lineLimit ? [...lines, word] : [...lines.slice(0, -1), joined];
  }, []);
  return lineWidths.some((width) => width > lineLimit) ? Infinity : lineWidths.length;
};

const candidatesFor = (sizes: NameAtSize[], canWrap: boolean): Candidate[] =>
  sizes.reduce<Candidate[]>(
    (candidates, size) =>
      candidates.concat(
        canWrap
          ? [
              { size, wraps: false },
              { size, wraps: true },
            ]
          : [{ size, wraps: false }],
      ),
    [],
  );

// Order of preference: the base size on one line, the base size on two lines,
// then 1px smaller on one line, on two lines, and so on down to 12px. The base
// size on one line is the only choice at scales where names never wrap, unless
// the name does not fit there either.
export const fitLevelName = (measure: LevelNameMeasure): LevelNameFit => {
  const { sizes, maxWidth, room } = measure;
  const [atBase] = sizes;
  const measured = sizes.flatMap((size) => [
    size.fontSize,
    size.oneLineWidth,
    size.lineHeight,
    ...size.wordWidths,
  ]);
  if (![...measured, room].every(Number.isFinite)) {
    return { fontSize: atBase.fontSize, wraps: false, pullTowardNode: 0 };
  }

  const heightOf = ({ size, wraps }: Candidate) => size.lineHeight * (wraps ? 2 : 1);
  // A wrapped name must really be two lines: a third line would grow past the
  // room worked out for two.
  const fitsAcross = ({ size, wraps }: Candidate) =>
    maxWidth === null ||
    (wraps ? wrappedLineCount(size, maxWidth) <= 2 : size.oneLineWidth <= maxWidth);
  const fitsUpDown = (candidate: Candidate) =>
    heightOf(candidate) + pathAreaEdgeGap <= room + maxPullTowardNode;

  const usableSizes = sizes.filter((size) => size === atBase || size.fontSize >= minFontSize);
  const candidates = candidatesFor(usableSizes, maxWidth !== null);
  const oneLineCandidates = candidates.filter((candidate) => !candidate.wraps);
  const chosen =
    candidates.find((candidate) => fitsAcross(candidate) && fitsUpDown(candidate)) ??
    oneLineCandidates[oneLineCandidates.length - 1];
  const pullTowardNode = Math.min(
    maxPullTowardNode,
    Math.max(0, heightOf(chosen) + pathAreaEdgeGap - room),
  );
  return { fontSize: chosen.size.fontSize, wraps: chosen.wraps, pullTowardNode };
};
